/* eslint-disable no-param-reassign,  @typescript-eslint/no-unused-vars, no-underscore-dangle */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { get } from '../../../services/GenesisApi';

import {
  GET_SWING_NOMS_BY_CONTRACT,
  GET_SWING_CONTRACTS_LITE,
  GET_SWING_NOMS_BY_CONTRACT_FOR_DATE,
} from '../../../constants/SwingNominationsByContracts';
import {
  IContract,
  INomsByContractDateRange,
} from '../../../types/SwingNomsByContract.type';

export interface ISwingNomsByContractState {
  loadingContracts: boolean;
  loadingForFindNow: boolean;
  loadingForModal: boolean;
  loadingForm: boolean;
  swingNomsForFindNow: INomsByContractDateRange | null;
  swingNomsForModal: INomsByContractDateRange | null;
  swingNomsByDate: INomsByContractDateRange | null;
  swingContractsLite: IContract[];
}

export const initialState: ISwingNomsByContractState = {
  loadingContracts: false,
  loadingForFindNow: false,
  loadingForModal: false,
  loadingForm: false,
  swingNomsForFindNow: null,
  swingNomsForModal: null,
  swingNomsByDate: null,
  swingContractsLite: [],
};

export const getSwingContractsLite = createAsyncThunk(
  'swingNomsByContract/getSwingContractsLite',
  async () => {
    return get(GET_SWING_CONTRACTS_LITE, {})
      .then((response) => {
        return response.data ?? [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getSwingNomsByContractForDateRange = createAsyncThunk(
  'swingNomsByContract/getSwingNomsByContractForDateRange',
  async ({
    startDate,
    endDate,
    contractId,
    _isFindNow,
  }: {
    startDate: string;
    endDate: string;
    contractId: number;
    _isFindNow: boolean;
  }) => {
    return get(
      `${GET_SWING_NOMS_BY_CONTRACT}?startDate=${startDate}&endDate=${endDate}&contractId=${contractId}`,
      {}
    )
      .then((response) => {
        return response.data ?? null;
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getSwingNomsByContractForDate = createAsyncThunk(
  'swingNomsByContract/getSwingNomsByContractForDate',
  async ({
    startDate,
    contractId,
  }: {
    startDate: string;
    contractId: number;
  }) => {
    return get(
      `${GET_SWING_NOMS_BY_CONTRACT_FOR_DATE}?date=${startDate}&contractId=${contractId}`,
      {}
    )
      .then((response) => {
        return response.data ?? null;
      })
      .catch((error) => {
        throw error;
      });
  }
);

const swingNomsByContract = createSlice({
  name: 'swingNomsByContract',
  initialState,
  reducers: {
    resetSwingNomsForFindNow: (state) => {
      state.swingNomsForFindNow = null;
    },
    resetSwingNomsForModal: (state) => {
      state.swingNomsForModal = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSwingContractsLite.pending, (state) => {
        state.loadingContracts = true;
      })
      .addCase(
        getSwingContractsLite.fulfilled,
        (state, { payload }: PayloadAction<IContract[]>) => {
          state.loadingContracts = false;
          const sortPayload = [...payload];
          sortPayload.sort((a, b) =>
            a.contractReference.localeCompare(b.contractReference)
          );
          state.swingContractsLite = sortPayload;
        }
      )
      .addCase(getSwingContractsLite.rejected, (state) => {
        state.loadingContracts = false;
        state.swingContractsLite = [];
      })
      .addCase(getSwingNomsByContractForDateRange.pending, (state, action) => {
        if (action.meta.arg._isFindNow) {
          state.loadingForFindNow = true;
        } else {
          state.loadingForModal = true;
        }
      })
      .addCase(
        getSwingNomsByContractForDateRange.fulfilled,
        (state, action) => {
          if (action.meta.arg._isFindNow) {
            state.swingNomsForFindNow = action.payload;
            state.loadingForFindNow = false;
          } else {
            state.swingNomsForModal = action.payload;
            state.loadingForModal = false;
          }
        }
      )
      .addCase(getSwingNomsByContractForDateRange.rejected, (state, action) => {
        if (action.meta.arg._isFindNow) {
          state.swingNomsForFindNow = null;
          state.loadingForFindNow = false;
        } else {
          state.swingNomsForModal = null;
          state.loadingForModal = false;
        }
      })
      .addCase(getSwingNomsByContractForDate.pending, (state) => {
        state.loadingForm = true;
      })
      .addCase(
        getSwingNomsByContractForDate.fulfilled,
        (state, { payload }: PayloadAction<INomsByContractDateRange>) => {
          state.loadingForm = false;
          state.swingNomsByDate = payload;
        }
      )
      .addCase(getSwingNomsByContractForDate.rejected, (state) => {
        state.loadingForm = false;
        state.swingNomsByDate = null;
      });
  },
});

export const swingContractActions = swingNomsByContract.actions;
export default swingNomsByContract.reducer;
